/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { Link } from "../components/ui/Link";
import { backHref } from "../lib/navigation";
import { Check, Users } from "lucide-react";
import {
  confirmMerge,
  dismissCase,
  getCase,
  SIGNALS,
  type AccountSide,
  type DuplicateCase,
} from "../api/duplicates";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { ScoreBadge } from "../components/duplicates/ScoreBadge";
import { CopyableId } from "../components/ui/CopyableId";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Modal } from "../components/ui/Modal";
import { Spinner } from "../components/ui/Spinner";
import { Textarea } from "../components/ui/Textarea";
import { useChrome } from "../contexts/ChromeContext";
import { useToast } from "../contexts/ToastContext";
import { cn } from "../lib/utils";
import { formatWhen, fullName, statusLabel, statusTone } from "../lib/accounts";

/**
 * One suspected pair, side by side. The reviewer picks the account that survives (the merged-away
 * account is disabled into the survivor's family, never deleted), writes down why, and confirms —
 * or dismisses, which silences the pair permanently. Differing fields are highlighted so the eye
 * lands on what actually disagrees.
 */
export function DuplicateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const [item, setItem] = useState<DuplicateCase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [survivor, setSurvivor] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState<"merge" | "dismiss" | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (id === undefined) {
      return;
    }
    void getCase(id).then((response) => {
      if (response.ok) {
        setItem(response.value);
        setError(null);
      } else {
        setError(response.error.message);
      }
    });
  }, [id]);

  useChrome({ title: "Duplicate review", back: { to: "/care/duplicates", label: "Duplicates" } }, []);

  if (error !== null) {
    return <ErrorMessage message={error} className="mt-8" />;
  }
  if (item === null || id === undefined) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const loser =
    survivor === null ? null : survivor === item.accountA.id ? item.accountB : item.accountA;

  async function resolve(kind: "merge" | "dismiss") {
    if (id === undefined || item === null) {
      return;
    }
    setBusy(true);
    const response =
      kind === "merge" && survivor !== null
        ? await confirmMerge(id, survivor, note.trim())
        : await dismissCase(id, note.trim());
    setBusy(false);
    setConfirming(null);
    if (response.ok) {
      showToast(
        "success",
        kind === "merge" ? "Accounts merged." : "Dismissed. This pair will not resurface.",
      );
      void navigate(backHref("/care/duplicates", location.state));
    } else {
      showToast("error", response.error.message);
    }
  }

  return (
    <div className="max-w-5xl space-y-6 pb-4">
      <header className="enter">
        <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
          Are these the same person?
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <ScoreBadge score={item.score} signals={item.signals} size="md" />
          {item.signals.map((signal) => (
            <span key={signal} className="text-xs text-text-tertiary">
              {SIGNALS[signal].label}
            </span>
          ))}
        </div>
      </header>

      <div className="enter grid gap-4 md:grid-cols-2">
        <SideCard
          side={item.accountA}
          other={item.accountB}
          selected={survivor === item.accountA.id}
          onKeep={() => setSurvivor(item.accountA.id)}
        />
        <SideCard
          side={item.accountB}
          other={item.accountA}
          selected={survivor === item.accountB.id}
          onKeep={() => setSurvivor(item.accountB.id)}
        />
      </div>

      <div className="enter space-y-3">
        <Textarea
          id="resolution-note"
          label="Resolution note"
          placeholder="What you verified and why"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="secondary"
            size="md"
            disabled={busy || note.trim() === ""}
            onClick={() => setConfirming("dismiss")}
          >
            Not the same person
          </Button>
          <Button
            size="md"
            disabled={busy || survivor === null || note.trim() === ""}
            onClick={() => setConfirming("merge")}
          >
            Merge into kept account
          </Button>
        </div>
      </div>

      <Modal
        isOpen={confirming === "merge"}
        onClose={() => setConfirming(null)}
        title="Merge accounts"
        size="sm"
      >
        <div className="space-y-4 px-6 py-5">
          <p className="text-sm leading-relaxed text-text-secondary">
            {loser !== null && (
              <>
                <span className="font-medium text-text-primary">
                  {fullName(loser.firstName, loser.lastName)}
                </span>{" "}
                (account {loser.id}) will be disabled and its patients, invites, and household
                move into the kept account's family. Sessions on the disabled account end
                immediately. This cannot be undone from the console.
              </>
            )}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button size="sm" disabled={busy} onClick={() => void resolve("merge")}>
              {busy ? <Spinner size="xs" className="mr-2" /> : null}
              Merge
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={confirming === "dismiss"}
        onClose={() => setConfirming(null)}
        title="Dismiss this pair"
        size="sm"
      >
        <div className="space-y-4 px-6 py-5">
          <p className="text-sm leading-relaxed text-text-secondary">
            These accounts will never be flagged as duplicates of each other again.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button size="sm" disabled={busy} onClick={() => void resolve("dismiss")}>
              Dismiss
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function SideCard({
  side,
  other,
  selected,
  onKeep,
}: {
  side: AccountSide;
  other: AccountSide;
  selected: boolean;
  onKeep: () => void;
}) {
  const differs = (pick: (s: AccountSide) => string | number | null) =>
    (pick(side) ?? "") !== (pick(other) ?? "");
  return (
    <section
      className={cn(
        "space-y-4 rounded-3xl border bg-bg-elevated p-5 shadow-card transition-colors",
        selected ? "border-accent/50 bg-accent/5" : "border-border",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate font-display text-lg leading-tight text-text-primary">
            {fullName(side.firstName, side.lastName)}
          </h2>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Badge tone={statusTone(side.status)}>{statusLabel(side.status)}</Badge>
            <CopyableId label="Account" value={String(side.id)} />
            {side.ehrUserId !== null && <CopyableId label="Healthie" value={side.ehrUserId} />}
          </div>
        </div>
        <Button
          variant={selected ? "primary" : "outline"}
          size="sm"
          aria-pressed={selected}
          onClick={onKeep}
        >
          {selected && <Check size={14} className="mr-1" />}
          {selected ? "Keeping" : "Keep"}
        </Button>
      </div>

      <dl className="space-y-1.5 text-sm">
        <Fact label="Email" highlight={differs((s) => s.email)}>
          {side.email ?? "—"}
        </Fact>
        <Fact label="Username" highlight={differs((s) => s.username)}>
          {side.username ?? "—"}
        </Fact>
        <Fact label="Phone" highlight={differs((s) => s.phone)}>
          {side.phone ?? "—"}
        </Fact>
        <Fact label="Created" highlight={false}>
          {formatWhen(side.createdAt)}
        </Fact>
      </dl>

      {side.familyId !== null && (
        <Link
          to={`/care/families/${side.familyId}`}
          className="inline-flex items-center gap-1.5 text-xs text-text-tertiary transition-colors hover:text-accent"
        >
          <Users size={12} />
          Family {side.familyId}
        </Link>
      )}
    </section>
  );
}

function Fact({
  label,
  highlight,
  children,
}: {
  label: string;
  highlight: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0 text-xs text-text-tertiary">{label}</dt>
      <dd
        className={cn(
          "truncate text-right",
          highlight ? "font-medium text-warning" : "text-text-secondary",
        )}
      >
        {children}
      </dd>
    </div>
  );
}
